import type { BoardUser, PdfContextRef, UserFile } from '../../commonTypes';
import type { Board } from '../../models/board.model';

export type PdfContextDocument = PdfContextRef & { file: UserFile };

const uniqueRefs = (refs: PdfContextRef[]): PdfContextRef[] => {
	const seen = new Set<string>();
	return refs.filter((ref) => {
		const key = `${ref.boardId}:${ref.fileId}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

export const defaultBoardPdfContext = (
	board: Pick<Board, '_id' | 'uploadedFiles'>
): PdfContextRef[] =>
	(board.uploadedFiles || []).map((file) => ({ boardId: board._id, fileId: file.fileId }));

export const selectedBoardPdfContext = (
	board: Pick<Board, '_id' | 'uploadedFiles'>,
	boardUser: Pick<BoardUser, 'pdfContext' | 'selectedFile'>
): PdfContextRef[] => {
	if (boardUser.pdfContext) return uniqueRefs(boardUser.pdfContext);
	if (board.uploadedFiles?.length) return defaultBoardPdfContext(board);
	return boardUser.selectedFile
		? [{ boardId: board._id, fileId: boardUser.selectedFile.fileId }]
		: [];
};

export const resolvePdfContextFiles = (
	board: Pick<Board, '_id' | 'uploadedFiles'>,
	boardUser: Pick<BoardUser, 'pdfContext' | 'selectedFile'>
): UserFile[] => {
	const refs = selectedBoardPdfContext(board, boardUser);
	const files = board.uploadedFiles || [];
	const byId = new Map(files.map((file) => [file.fileId, file]));
	const resolved = refs
		.map((ref) => {
			if (ref.boardId !== board._id) return undefined;
			if (byId.has(ref.fileId)) return byId.get(ref.fileId);
			if (boardUser.selectedFile?.fileId === ref.fileId) return boardUser.selectedFile;
			return undefined;
		})
		.filter((file): file is UserFile => Boolean(file));
	if (resolved.length) return resolved;
	return boardUser.selectedFile ? [boardUser.selectedFile] : [];
};

export const appendUploadedFilesToPdfContext = (
	boardId: string,
	boardUser: BoardUser,
	previousFiles: UserFile[],
	newFiles: UserFile[]
): BoardUser => {
	const prior = boardUser.pdfContext ||
		previousFiles.map((file) => ({ boardId, fileId: file.fileId }));
	return {
		...boardUser,
		selectedFile: boardUser.selectedFile || newFiles[0],
		pdfContext: uniqueRefs([
			...prior,
			...newFiles.map((file) => ({ boardId, fileId: file.fileId })),
		]),
	};
};
